import { PayloadAction } from '@reduxjs/toolkit';

import { MapMarkerIconVariant } from '~components/commons/MapMarkerIcon/MapMarkerIcon';

import actionTypes from '~actionTypes';
import { ShiftsWithDeliveriesData } from '~api/common/stores/getShiftsWithDeliveries';
import { ORDER_DELIVERY_TYPE_VALUE, OrderDeliveryTag } from '~constants/delivery';
import { createNamespacer, createReducer, ReducerHandlers } from '~utils/reducers';

export interface Location {
	id: number;
	name: string;
	address: string;
	city: string;
	state: string;
	zipCode: string;
	timeZone: string;
	lat: number;
	lng: number;
	deliveryEnabled: boolean;
}

export interface StopItemDetails {
	id: number;
	lat: number;
	lng: number;
	address?: string;
	customerName?: string | null;
	orderCode?: string | null;
	type?: ORDER_DELIVERY_TYPE_VALUE;
	bagsCount?: number;
	hangerBundlesCount?: number;
	tags?: OrderDeliveryTag[];
}

export interface StopPoint extends StopItemDetails {
	id: number;
	color: string;
	stopNumber?: number;
	mapMarkerIconVariant?: MapMarkerIconVariant;
}

export const enum SetStartEndpointSource {
	STORE = 'store',
	ROUTE = 'route',
	CUSTOM = 'custom',
}

export type StartEndPoint = {
	latitude: number | null;
	longitude: number | null;
	source?: SetStartEndpointSource;
	address: string;
	orderDeliveryId?: number;
};

export type DispatchState = {
	activeLocation: Location | null;
	mapStops: StopPoint[];
	shifts: ShiftsWithDeliveriesData[];
	newRoute: {
		selectedDate: string | null;
		selectedDeliveries: Record<number, boolean>;
		selectedStartPoint: StartEndPoint | null;
		selectedEndPoint: StartEndPoint | null;
	};
	editRoute: {
		selectedDeliveries: Record<number, boolean>;
	};
};

const dispatchNameSpace = 'BO-DISPATCH' as const;

export const dispatchNameSpacer = createNamespacer(dispatchNameSpace);
export const dispatchAT = actionTypes.businessOwner.dispatch;
export const dispatchNewRouteAT = actionTypes.businessOwner.dispatch.newRoute;
export const dispatchEditRouteAT = actionTypes.businessOwner.dispatch.editRoute;

const initialState: DispatchState = {
	activeLocation: null,
	mapStops: [],
	shifts: [],
	newRoute: {
		selectedDate: null,
		selectedDeliveries: {},
		selectedStartPoint: null,
		selectedEndPoint: null,
	},
	editRoute: {
		selectedDeliveries: {},
	},
};

const handlers: ReducerHandlers<DispatchState> = {
	[dispatchNameSpacer(dispatchAT.SET_ACTIVE_LOCATION)]: (
		_state,
		action: PayloadAction<Location | null>,
	) => {
		return {
			...initialState,
			activeLocation: action.payload,
		};
	},
	[dispatchNameSpacer(dispatchAT.SET_MAP_STOPS)]: (
		state,
		action: PayloadAction<StopPoint[]>,
	) => {
		return {
			...state,
			mapStops: action.payload,
		};
	},
	[dispatchNameSpacer(dispatchAT.SET_SHIFTS)]: (
		state,
		action: PayloadAction<ShiftsWithDeliveriesData[]>,
	) => {
		return {
			...state,
			shifts: action.payload,
		};
	},

	/** New Route Actions */
	[dispatchNameSpacer(dispatchNewRouteAT.SET_DATE)]: (
		state,
		action: PayloadAction<string | null>,
	) => {
		return {
			...state,
			newRoute: {
				...state.newRoute,
				selectedDate: action.payload,
			},
		};
	},
	[dispatchNameSpacer(dispatchNewRouteAT.SET_START_POINT)]: (
		state,
		action: PayloadAction<StartEndPoint | null>,
	) => {
		return {
			...state,
			newRoute: {
				...state.newRoute,
				selectedStartPoint: action.payload,
			},
		};
	},
	[dispatchNameSpacer(dispatchNewRouteAT.SET_END_POINT)]: (
		state,
		action: PayloadAction<StartEndPoint | null>,
	) => {
		return {
			...state,
			newRoute: {
				...state.newRoute,
				selectedEndPoint: action.payload,
			},
		};
	},
	[dispatchNameSpacer(dispatchNewRouteAT.TOGGLE_SHIFT)]: (
		state,
		action: PayloadAction<ShiftsWithDeliveriesData>,
	) => {
		const shift = action.payload;

		const isAllShiftDeliveriesSelected =
			shift.orderDeliveries.length > 0 &&
			shift.orderDeliveries.every(
				(delivery) => state.newRoute.selectedDeliveries[delivery.id],
			);

		const foundShift = state.shifts.find((s) => s.id === shift.id);
		if (!foundShift) {
			return state;
		}

		const newSelectedDeliveries = { ...state.newRoute.selectedDeliveries };

		if (!isAllShiftDeliveriesSelected) {
			// Select all deliveries of this shift
			for (const delivery of shift.orderDeliveries) {
				newSelectedDeliveries[delivery.id] = true;
			}
		} else {
			// Deselect all deliveries of this shift
			for (const delivery of shift.orderDeliveries) {
				delete newSelectedDeliveries[delivery.id];
			}
		}

		return {
			...state,
			newRoute: {
				...state.newRoute,
				selectedDeliveries: newSelectedDeliveries,
			},
		};
	},
	[dispatchNameSpacer(dispatchNewRouteAT.UPDATE_SELECTIONS)]: (
		state,
		action: PayloadAction<Record<number, boolean>>,
	) => {
		const selections = action.payload;
		const updatedSelectedDeliveries = { ...state.newRoute.selectedDeliveries };

		for (const id in selections) {
			updatedSelectedDeliveries[id] = selections[id];
		}
		return {
			...state,
			newRoute: {
				...state.newRoute,
				selectedDeliveries: updatedSelectedDeliveries,
			},
		};
	},
	[dispatchNameSpacer(dispatchNewRouteAT.CLEAR_SELECTIONS)]: (state) => {
		return {
			...state,
			newRoute: initialState.newRoute,
		};
	},

	/** Edit Route Actions */
	[dispatchNameSpacer(dispatchEditRouteAT.TOGGLE_SHIFT)]: (
		state,
		action: PayloadAction<ShiftsWithDeliveriesData>,
	) => {
		const shift = action.payload;

		const isAllShiftDeliveriesSelected =
			shift.orderDeliveries.length > 0 &&
			shift.orderDeliveries.every(
				(delivery) => state.editRoute.selectedDeliveries[delivery.id],
			);

		const foundShift = state.shifts.find((s) => s.id === shift.id);
		if (!foundShift) {
			return state;
		}

		const newSelectedDeliveries = { ...state.editRoute.selectedDeliveries };

		if (!isAllShiftDeliveriesSelected) {
			// Select all deliveries of this shift
			for (const delivery of shift.orderDeliveries) {
				newSelectedDeliveries[delivery.id] = true;
			}
		} else {
			// Deselect all deliveries of this shift
			for (const delivery of shift.orderDeliveries) {
				delete newSelectedDeliveries[delivery.id];
			}
		}

		return {
			...state,
			editRoute: {
				...state.editRoute,
				selectedDeliveries: newSelectedDeliveries,
			},
		};
	},
	[dispatchNameSpacer(dispatchEditRouteAT.UPDATE_SELECTIONS)]: (
		state,
		action: PayloadAction<Record<number, boolean>>,
	) => {
		const selections = action.payload;
		const updatedSelectedDeliveries = { ...state.editRoute.selectedDeliveries };

		for (const id in selections) {
			updatedSelectedDeliveries[id] = selections[id];
		}
		return {
			...state,
			editRoute: {
				...state.editRoute,
				selectedDeliveries: updatedSelectedDeliveries,
			},
		};
	},
	[dispatchNameSpacer(dispatchEditRouteAT.CLEAR_SELECTIONS)]: (state) => {
		return {
			...state,
			editRoute: initialState.editRoute,
		};
	},
};

export default createReducer(initialState, handlers, [dispatchNameSpace]);
